import React, { useEffect, useRef, useState } from 'react';
import { Clapperboard, Brain, PenLine, Megaphone, UserCircle, Camera, FileText, Search, GraduationCap, Wallet, Users } from 'lucide-react';

// Map each roster persona to a lucide icon at render time (visual only — the
// backend still sends an emoji string; we just never render it).
function personaIcon(role?: string) {
  const k = (role || '').toLowerCase();
  if (k.includes('creative') && k.includes('director')) return Clapperboard;
  if (k.includes('strateg')) return Brain;
  if (k.includes('script')) return PenLine;
  if (k.includes('character')) return UserCircle;
  if (k.includes('shot')) return Camera;
  if (k.includes('prompt')) return FileText;
  if (k.includes('critic')) return Search;
  if (k.includes('learn')) return GraduationCap;
  if (k.includes('finance')) return Wallet;
  if (k.includes('director')) return Megaphone;
  return Users;
}

// ── Top-down animated pixel office ────────────────────────────────────────────
// A real little world: 9 pixel coworkers (men + women) who WALK. When a job runs
// they head to their desks and type; when a persona finishes they walk over to the
// next one to hand off; when idle the whole team drifts to the lounge and hangs out.
// Fully driven by /creative-team/activity. Crisp names via a DOM overlay that follows
// each character (canvas text stays blocky, so labels live in the DOM).

interface Desk {
  id: string; role: string; emoji: string;
  status: 'idle' | 'queued' | 'working'; task: string | null;
  last: { ok: boolean; revised: boolean; detail: string } | null;
  // null/undefined = the engine never said. used:false = this persona's plan was DISCARDED for
  // this job, so feedback aimed at it is recorded but cannot change rendered output.
  authority?: { used: boolean; note?: string } | null;
}

const W = 680, H = 470, S = 2;   // internal canvas + sprite pixel scale

// role → look + home desk. Genders are a real mix; glasses drawn as thin frames.
const META: Record<string, any> = {
  director:    { gender: 'male',   skin: '#e8b088', hair: '#2f2118', style: 'short', shirt: '#e0a030', pants: '#6b4a2f', desk: { x: 300, y: 96 } },
  strategist:  { gender: 'female', skin: '#f1c19a', hair: '#5a3a22', style: 'long',  shirt: '#3b82f6', pants: '#334155', desk: { x: 90,  y: 220 }, glasses: true },
  scriptwriter:{ gender: 'male',   skin: '#d99a6c', hair: '#4a2f1a', style: 'short', shirt: '#8b5cf6', pants: '#3f3f46', desk: { x: 210, y: 220 } },
  scene:       { gender: 'female', skin: '#e8b088', hair: '#241610', style: 'bun',   shirt: '#ef4444', pants: '#4b5563', desk: { x: 330, y: 220 } },
  character:   { gender: 'male',   skin: '#c98a5a', hair: '#231508', style: 'short', shirt: '#10b981', pants: '#374151', desk: { x: 450, y: 220 } },
  shots:       { gender: 'female', skin: '#f1c19a', hair: '#3a1a2a', style: 'long',  shirt: '#ec4899', pants: '#334155', desk: { x: 90,  y: 340 } },
  prompt:      { gender: 'male',   skin: '#e8b088', hair: '#5a3a1a', style: 'short', shirt: '#eab308', pants: '#3f3f46', desk: { x: 210, y: 340 } },
  critic:      { gender: 'female', skin: '#d99a6c', hair: '#1a1a1a', style: 'long',  shirt: '#6366f1', pants: '#4b5563', desk: { x: 330, y: 340 }, glasses: true },
  learner:     { gender: 'male',   skin: '#e8b088', hair: '#4a3a1a', style: 'short', shirt: '#06b6d4', pants: '#374151', desk: { x: 450, y: 340 } },
  finance:     { gender: 'female', skin: '#e8b088', hair: '#2a1e12', style: 'bun',   shirt: '#f59e0b', pants: '#334155', desk: { x: 450, y: 96 }, glasses: true },
};
const ORDER = ['director', 'strategist', 'scriptwriter', 'scene', 'character', 'shots', 'prompt', 'critic', 'learner', 'finance'];
const LOUNGE = { x: 575, y: 300, r: 78 };   // where they gather when idle
// small conversational groups for idle time — spread across the floor so they read as
// three distinct clusters hanging out (not one overlapping pile), people facing each other.
const CLUSTERS = [
  { cx: 168, cy: 405, members: ['director', 'strategist', 'scriptwriter'] },   // left, by the plant
  { cx: 360, cy: 420, members: ['scene', 'character', 'shots'] },              // center-bottom
  { cx: 575, cy: 300, members: ['prompt', 'critic', 'learner', 'finance'] },  // lounge sofas (right)
];

// ── Rooms: one floor plan, several camera framings ────────────────────────────
// Tabs don't swap scenes — they pan/zoom the SAME world, so the team stays continuous and you
// never lose track of who is where. `all` is the default wide shot.
export const ROOMS = [
  { id: 'all',        name: 'Floor',       x: 0,   y: 0,   w: W,   h: H },
  { id: 'bullpen',    name: 'Bullpen',     x: 40,  y: 180, w: 460, h: 250 },
  { id: 'conference', name: 'Conference',  x: 210, y: 46,  w: 300, h: 150 },
  { id: 'lounge',     name: 'Break room',  x: 480, y: 140, w: 200, h: 310 },
] as const;
export type RoomId = typeof ROOMS[number]['id'];

// Conference table — where everyone sits when a meeting is called.
const TABLE = { x: 268, y: 86, w: 150, h: 52 };
// Seats around the table, filled in roster order.
const SEATS = [
  { x: 286, y: 74 }, { x: 320, y: 74 }, { x: 354, y: 74 }, { x: 388, y: 74 },
  { x: 286, y: 150 }, { x: 320, y: 150 }, { x: 354, y: 150 }, { x: 388, y: 150 },
  { x: 256, y: 112 }, { x: 430, y: 112 }, { x: 256, y: 138 },
];

// Interior wall segments [x, y, w, h] — what turns an open floor into a floor PLAN.
const WALLS: Array<[number, number, number, number]> = [
  [206, 40, 4, 60], [206, 128, 4, 40],          // conference: left wall, doorway gap at y=100..128
  [508, 40, 4, 70], [508, 140, 4, 30],          // conference: right wall + gap
  [210, 166, 130, 4], [378, 166, 130, 4],       // conference: bottom wall, centre doorway
  [472, 140, 4, 120], [472, 300, 4, 150],       // break room: left wall with doorway
  [40, 176, 200, 4], [300, 176, 200, 4],        // bullpen: top divider with a corridor gap
];

const px = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: string) => {
  ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
};

// ── a walking top-down character. (cx,cy) = feet centre. dir ∈ down/up/left/right ──
// ── Sprite frames ─────────────────────────────────────────────────────────────
// The walk used to be 2 frames (`step % 2`), which reads as a twitch rather than a stride.
// WALK_FRAMES is a real cycle: contact → passing → contact on the other foot, with the body
// bobbing and the arms counter-swinging, so movement has weight.
const WALK_FRAMES = 6;
// Per-frame leg offsets (front/back foot) and body bob, in sprite units.
const GAIT = [
  { front: 2, back: -2, bob: 0 },
  { front: 1, back: -1, bob: -1 },
  { front: 0, back: 0, bob: -1 },
  { front: -2, back: 2, bob: 0 },
  { front: -1, back: 1, bob: -1 },
  { front: 0, back: 0, bob: -1 },
];

// Cell geometry for one cached sprite frame. The body spans x 2..10, y 0..20 in sprite units;
// CELL_* leaves room for the bob offset so no frame clips at the edges.
const CELL_W = 14, CELL_H = 23, CELL_OX = 1, CELL_OY = 2;

function drawCharBody(ctx: CanvasRenderingContext2D, ox: number, oy: number, dir: string, step: number, m: any, sitting: boolean, working: boolean) {
  const g = GAIT[((step % WALK_FRAMES) + WALK_FRAMES) % WALK_FRAMES];
  const bob = sitting ? 0 : g.bob;
  oy += bob * S;
  const P = (x: number, y: number, w: number, h: number, c: string) => px(ctx, ox + x * S, oy + y * S, w * S, h * S, c);

  const legY = 15;
  if (!sitting) {
    // Legs swing in opposition across the 6-frame cycle instead of flicking between two poses.
    const lf = Math.round(g.front / 2), lb = Math.round(g.back / 2);
    P(3, legY - lf, 3, 5 + lf, m.pants);
    P(6, legY - lb, 3, 5 + lb, m.pants);
    P(3, legY + 4 - lf, 3, 1, '#222');
    P(6, legY + 4 - lb, 3, 1, '#222');
  }
  // torso (males a touch broader)
  const bw = m.gender === 'male' ? 9 : 8, bx = 6 - bw / 2;
  P(bx, 10, bw, 6, m.shirt);
  P(bx, 10, bw, 1, 'rgba(255,255,255,.15)');
  // arms counter-swing to the legs; typing overrides with a small hand flutter
  const swingF = working ? (step % 2 ? 1 : 0) : Math.round(-g.front / 2);
  const swingB = working ? (step % 2 ? 0 : 1) : Math.round(-g.back / 2);
  P(bx - 1, 11 + swingF, 2, 4, m.skin);
  P(bx + bw - 1, 11 + swingB, 2, 4, m.skin);

  // head + hair by direction
  const back = dir === 'up';
  P(3, 3, 6, 7, m.skin);                             // head
  // hair
  if (m.style === 'long' && !back) { P(2, 2, 8, 5, m.hair); P(2, 4, 2, 6, m.hair); P(8, 4, 2, 6, m.hair); }
  else if (m.style === 'bun') { P(3, 2, 6, 3, m.hair); P(5, 0, 3, 2, m.hair); }
  else { P(3, 2, 6, 3, m.hair); P(3, 2, 1, 4, m.hair); P(8, 2, 1, 4, m.hair); }
  if (back) { P(3, 2, 6, 8, m.hair); }               // back of head = all hair

  if (!back) {                                        // face
    const ex = dir === 'left' ? -1 : dir === 'right' ? 1 : 0;
    P(4 + ex, 6, 1, 1, '#2b2b2b'); P(7 + ex, 6, 1, 1, '#2b2b2b');
    if (m.glasses) { P(4 + ex, 6, 5, 1, '#2b3a4a'); }     // thin frame (NOT blacked-out eyes)
    P(5 + ex, 8, 2, 1, '#b56b62');                    // mouth
  }
}

// ── Offscreen sprite cache ────────────────────────────────────────────────────
// Every character used to be rebuilt from ~20 fillRect calls on EVERY animation frame — 10
// characters x 60fps. Each distinct pose is now rasterised ONCE into a small offscreen canvas and
// blitted thereafter, which is what makes a 6-frame gait affordable. Lazy: only poses that
// actually occur are ever built.
const _spriteCache = new Map<string, HTMLCanvasElement>();

function spriteFor(key: string, dir: string, step: number, m: any, sitting: boolean, working: boolean): HTMLCanvasElement {
  const k = `${key}|${dir}|${sitting ? 's' : 'w'}${working ? 'k' : ''}|${sitting ? 0 : ((step % WALK_FRAMES) + WALK_FRAMES) % WALK_FRAMES}`;
  const hit = _spriteCache.get(k);
  if (hit) return hit;
  const cv = document.createElement('canvas');
  cv.width = CELL_W * S; cv.height = CELL_H * S;
  const c2 = cv.getContext('2d')!;
  c2.imageSmoothingEnabled = false;
  drawCharBody(c2, CELL_OX * S, CELL_OY * S, dir, step, m, sitting, working);
  _spriteCache.set(k, cv);
  return cv;
}

function drawChar(ctx: CanvasRenderingContext2D, cx: number, cy: number, dir: string, step: number, m: any, sitting: boolean, working: boolean, key = '') {
  const g = GAIT[((step % WALK_FRAMES) + WALK_FRAMES) % WALK_FRAMES];
  const bob = sitting ? 0 : g.bob;
  // Shadow stays on the ground while the body bobs — that contrast is what sells the step, so it
  // is drawn live rather than baked into the sprite.
  ctx.fillStyle = 'rgba(30,41,59,.10)'; ctx.beginPath();
  ctx.ellipse(cx, cy + 2, (8 - (bob ? 0.6 : 0)) * S, 2.7 * S, 0, 0, Math.PI * 2); ctx.fill();
  const sp = spriteFor(key || `${m.shirt}${m.hair}${m.skin}${m.style}${m.gender}${m.glasses ? 'g' : ''}`,
                       dir, step, m, sitting, working);
  ctx.drawImage(sp, Math.round(cx - (6 + CELL_OX) * S), Math.round(cy - (20 + CELL_OY) * S));
}

function drawDeskTop(ctx: CanvasRenderingContext2D, m: any, working: boolean, frame: number) {
  const d = m.desk;
  ctx.fillStyle = 'rgba(0,0,0,.10)'; ctx.fillRect(d.x - 22, d.y + 10, 44, 5);
  px(ctx, d.x - 22, d.y - 4, 44, 12, '#8a6340');       // desk top
  px(ctx, d.x - 22, d.y + 6, 44, 2, '#6b4a2f');
  // monitor
  px(ctx, d.x - 9, d.y - 14, 18, 12, '#20242b');
  const glow = working ? (Math.floor(frame / 5) % 2 ? '#8fe9ff' : '#38bdf8') : '#274152';
  px(ctx, d.x - 7, d.y - 12, 14, 8, glow);
  if (working) { ctx.fillStyle = 'rgba(255,255,255,.25)'; for (let i = 0; i < 3; i++) px(ctx, d.x - 6, d.y - 11 + ((frame + i * 3) % 7), 12, 1, '#cfeffb'); }
}

// time-of-day theme: window + wall/floor tint + ambient overlay
function _phase() {
  const h = new Date().getHours();
  if (h >= 6 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 20) return 'evening';
  return 'night';
}
const THEME: Record<string, any> = {
  morning:   { floor: '#eef2f8', wall: '#dbe4f0', sky: '#bfe3f5', overlay: 'rgba(255,246,224,0.10)' },
  afternoon: { floor: '#e9edf4', wall: '#d3dbe7', sky: '#a7d8f0', overlay: 'rgba(255,248,232,0.06)' },
  evening:   { floor: '#efe6de', wall: '#e6cdb8', sky: '#f4a15b', overlay: 'rgba(255,150,70,0.12)' },
  // night = a LIT office at night (objects stay visible); a cool tint + dark window convey the hour.
  // True darkness is the 💡 toggle (lights off), not the phase.
  night:     { floor: '#c7cddd', wall: '#aab2c8', sky: '#0e1430', overlay: 'rgba(26,38,74,0.16)' },
};

export function PixelOffice({ desks, cur, onSelectPersona, room = 'all', meeting = false }: {
  desks: Desk[]; feed?: any[]; cur?: any; onSelectPersona?: (id: string) => void;
  room?: RoomId; meeting?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const labelRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [lightsOn, setLightsOn] = useState(true);
  const [phase] = useState(_phase);
  const stateRef = useRef({ desks, cur, lightsOn, phase, room, meeting });
  stateRef.current = { desks, cur, lightsOn, phase, room, meeting };
  // Camera lerps toward the active room instead of cutting, so switching tabs reads as a pan.
  const cam = useRef({ x: 0, y: 0, s: 1, tx: 0, ty: 0, ts: 1 });
  const agents = useRef<Record<string, any>>({});
  const prevWorking = useRef<string | null>(null);

  // init agents once (start seated at desks)
  if (!Object.keys(agents.current).length) {
    CLUSTERS.forEach(cl => cl.members.forEach((id, j) => {
      const m = META[id];
      const ang = (j / cl.members.length) * Math.PI * 2 - Math.PI / 2;
      agents.current[id] = {
        id, x: m.desk.x, y: m.desk.y + 22, tx: m.desk.x, ty: m.desk.y + 22,
        dir: 'down', step: 0, sitting: true, mode: 'desk', bubble: '', phase: (j + 1) * 0.7,
        lounge: { x: cl.cx + Math.cos(ang) * 36, y: cl.cy + Math.sin(ang) * 26 },
        cluster: { x: cl.cx, y: cl.cy },
      };
    }));
  }

  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    let raf = 0, frame = 0, stopped = false;

    const render = () => {
      if (stopped) return;
      frame++;
      const { desks: ds, cur: c } = stateRef.current;
      const running = c?.status === 'running';
      const byId: Record<string, Desk> = {}; (ds || []).forEach(d => (byId[d.id] = d));
      const workingId = (ds || []).find(d => d.status === 'working')?.id || null;

      // ── behavior: decide each agent's target ──
      // handover: when the working persona changes, send the PREVIOUS one over to the new desk
      if (running && workingId && prevWorking.current && prevWorking.current !== workingId) {
        const a = agents.current[prevWorking.current];
        if (a) { a.mode = 'handover'; a.handTo = workingId; a.handBack = 40; }
      }
      if (running) prevWorking.current = workingId || prevWorking.current;
      else prevWorking.current = null;

      const inMeeting = stateRef.current.meeting;

      for (const id of ORDER) {
        const a = agents.current[id]; const m = META[id]; const st = byId[id];
        if (inMeeting) {
          // Meeting called: everyone walks to the conference table and takes a seat.
          const seat = SEATS[ORDER.indexOf(id) % SEATS.length];
          a.mode = 'meeting'; a.tx = seat.x; a.ty = seat.y;
          const seated = Math.hypot(a.x - a.tx, a.y - a.ty) < 3;
          a.sitting = seated;
          if (seated) a.dir = seat.y < TABLE.y + TABLE.h / 2 ? 'down' : 'up';
          a.bubble = seated ? ((Math.floor(frame / 60 + a.phase * 4) % 4 === 0) ? '💬' : '') : '';
        } else if (a.mode === 'handover' && a.handTo) {
          const td = META[a.handTo].desk; a.tx = td.x - 26; a.ty = td.y + 22; a.sitting = false; a.bubble = '📋';
          if (Math.hypot(a.x - a.tx, a.y - a.ty) < 4) { a.handBack--; if (a.handBack <= 0) { a.mode = running ? 'desk' : 'lounge'; a.handTo = null; a.bubble = ''; } }
        } else if (running && id === 'director') {
          // the director strolls between the desks, overseeing the work
          const PTS = [{ x: 110, y: 150 }, { x: 470, y: 150 }, { x: 470, y: 272 }, { x: 110, y: 272 }, { x: 290, y: 200 }];
          if (a.patrolI == null || Math.hypot(a.x - PTS[a.patrolI].x, a.y - PTS[a.patrolI].y) < 6)
            a.patrolI = ((a.patrolI ?? -1) + 1) % PTS.length;
          a.tx = PTS[a.patrolI].x; a.ty = PTS[a.patrolI].y; a.sitting = false;
          a.bubble = (Math.floor(frame / 80) % 3 === 0) ? '👀' : '';
        } else if (running) {
          a.mode = 'desk'; a.tx = m.desk.x; a.ty = m.desk.y + 22;
          const atDesk = Math.hypot(a.x - a.tx, a.y - a.ty) < 3;
          a.sitting = atDesk;
          a.bubble = atDesk && st?.status === 'working' ? (st.task ? '💬' : '⌨') : (atDesk && st?.last?.ok ? '✓' : '');
        } else {
          // idle: mingle in a small conversational group, facing the others
          a.mode = 'lounge';
          const drift = Math.sin((frame + a.phase * 60) / 60) * 4;
          a.tx = a.lounge.x + drift; a.ty = a.lounge.y + Math.cos((frame + a.phase * 60) / 70) * 3;
          a.sitting = false;
          if (Math.hypot(a.x - a.tx, a.y - a.ty) < 6 && a.cluster) {
            const dx = a.cluster.x - a.x, dy = a.cluster.y - a.y;
            a.dir = Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
          }
          a.bubble = (Math.floor(frame / 70 + a.phase * 5) % 5 === 0) ? (['😄', '💬', '☕', '👍', '😂'][Math.floor((frame / 70 + a.phase * 5)) % 5]) : '';
        }
        // move toward target
        const dx = a.tx - a.x, dy = a.ty - a.y, dist = Math.hypot(dx, dy);
        if (dist > 1.2 && !a.sitting) {
          const sp = Math.min(1.7, dist); a.x += (dx / dist) * sp; a.y += (dy / dist) * sp;
          a.dir = Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
          if (frame % 6 === 0) a.step++;
        } else if (a.sitting) { a.dir = 'down'; }
      }

      // ── camera: lerp toward the active room so tab switches read as a pan, not a cut ──
      {
        const rid = stateRef.current.meeting ? 'conference' : stateRef.current.room;
        const r = ROOMS.find(z => z.id === rid) || ROOMS[0];
        const pad = 12;
        const s = Math.min(W / (r.w + pad * 2), H / (r.h + pad * 2));
        cam.current.ts = s;
        cam.current.tx = r.x + r.w / 2;
        cam.current.ty = r.y + r.h / 2;
        const k = 0.08;   // easing
        cam.current.s += (cam.current.ts - cam.current.s) * k;
        cam.current.x += (cam.current.tx - cam.current.x) * k;
        cam.current.y += (cam.current.ty - cam.current.y) * k;
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.translate(W / 2, H / 2);
      ctx.scale(cam.current.s, cam.current.s);
      ctx.translate(-cam.current.x, -cam.current.y);

      // ── draw world (time-of-day themed) ──
      const th = THEME[stateRef.current.phase] || THEME.afternoon;
      const night = stateRef.current.phase === 'night';
      px(ctx, 0, 0, W, H, th.floor);
      px(ctx, 0, 0, W, 30, th.wall); px(ctx, 0, 30, W, 2, night ? '#0b0f1e' : '#c0cad9');   // back wall
      // window with sun / sunset / moon+stars
      px(ctx, 26, 6, 58, 20, th.sky);
      if (stateRef.current.phase === 'night') {
        px(ctx, 66, 9, 9, 9, '#e8ecf5'); px(ctx, 66, 9, 4, 9, th.sky);          // crescent moon
        px(ctx, 34, 11, 1, 1, '#fff'); px(ctx, 45, 20, 1, 1, '#fff'); px(ctx, 52, 13, 1, 1, '#fff');  // stars
      } else if (stateRef.current.phase === 'evening') {
        px(ctx, 44, 16, 12, 6, '#ffd27f'); px(ctx, 47, 14, 8, 8, '#ffb454');    // low setting sun
      } else {
        px(ctx, 30, 9, 10, 10, '#fde68a');                                       // sun
      }
      px(ctx, 53, 6, 2, 20, night ? '#0b0f1e' : '#c0cad9');                       // window mullion
      ctx.fillStyle = night ? '#5b6b8f' : '#8792a8'; ctx.font = '10px monospace'; ctx.textAlign = 'right'; ctx.fillText('ORBIT', W - 10, 20);
      // floor tiles
      ctx.strokeStyle = 'rgba(148,163,184,.16)';
      for (let gx = 0; gx <= W; gx += 34) { ctx.beginPath(); ctx.moveTo(gx, 32); ctx.lineTo(gx, H); ctx.stroke(); }
      for (let gy = 32; gy <= H; gy += 34) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke(); }
      // ── interior walls: what makes this a floor PLAN and not one open room ──
      for (const [wx, wy, ww, wh] of WALLS) {
        px(ctx, wx, wy, ww, wh, night ? '#5a6480' : '#b9c3d4');
        px(ctx, wx, wy, ww, Math.min(2, wh), night ? '#6d7794' : '#d3dbe7');   // top highlight
      }

      // conference room: long table + chairs down both sides
      px(ctx, TABLE.x, TABLE.y, TABLE.w, TABLE.h, '#b5824a');
      px(ctx, TABLE.x, TABLE.y, TABLE.w, 5, '#c99a63');
      px(ctx, TABLE.x + 6, TABLE.y + 10, TABLE.w - 12, TABLE.h - 20, 'rgba(255,255,255,.07)');
      for (const st of SEATS) px(ctx, st.x - 5, st.y - 4, 10, 8, '#7c5cff');
      ctx.fillStyle = night ? '#5b6b8f' : '#94a2ba'; ctx.font = '7px monospace'; ctx.textAlign = 'center';
      ctx.fillText('CONFERENCE', TABLE.x + TABLE.w / 2, TABLE.y - 6);
      ctx.textAlign = 'left';

      // lounge room (right): rug + sofas + plant + coffee
      px(ctx, 494, 150, 172, 290, 'rgba(99,102,241,.06)');
      ctx.strokeStyle = 'rgba(99,102,241,.25)'; ctx.strokeRect(494, 150, 172, 290);
      ctx.fillStyle = 'rgba(217,119,87,.18)'; ctx.beginPath(); ctx.ellipse(LOUNGE.x, LOUNGE.y, 96, 82, 0, 0, 7); ctx.fill();
      px(ctx, 500, 168, 34, 14, '#7c5cff'); px(ctx, 500, 164, 34, 6, '#6d28d9');   // sofa
      px(ctx, 630, 168, 34, 14, '#7c5cff'); px(ctx, 630, 164, 34, 6, '#6d28d9');
      px(ctx, 636, 405, 10, 16, '#9aa3b2'); px(ctx, 634, 400, 14, 6, '#c2ccdb');   // water cooler
      // plants
      const plant = (x: number, y: number, f: number) => { const s = Math.sin((f + x) / 30) * 2; px(ctx, x, y, 12, 8, '#b06a3a'); px(ctx, x + 3 + s, y - 9, 3, 10, '#3f9d5a'); px(ctx, x + 7 + s, y - 12, 3, 13, '#3f9d5a'); };
      plant(18, 250, frame); plant(465, 420, frame + 30); plant(500, 420, frame + 60);

      // desks (behind chars)
      for (const id of ORDER) drawDeskTop(ctx, META[id], byId[id]?.status === 'working', frame);
      // agents painter-sorted by y
      const sorted = [...ORDER].sort((p, q) => agents.current[p].y - agents.current[q].y);
      for (const id of sorted) { const a = agents.current[id]; drawChar(ctx, a.x, a.y, a.dir, a.step, META[id], a.sitting, byId[id]?.status === 'working', id); }

      ctx.restore();   // world done — everything below is SCREEN space (tint, vignette, progress)

      // ── ambient lighting: window beam (day) + time-of-day tint + lights on/off ──
      if (!night) {
        const beam = ctx.createLinearGradient(30, 30, 210, 300);
        beam.addColorStop(0, stateRef.current.phase === 'evening' ? 'rgba(255,150,70,0.20)' : 'rgba(255,241,204,0.16)');
        beam.addColorStop(1, 'rgba(255,241,204,0)');
        ctx.fillStyle = beam; ctx.beginPath();
        ctx.moveTo(26, 32); ctx.lineTo(92, 32); ctx.lineTo(230, 300); ctx.lineTo(74, 300); ctx.closePath(); ctx.fill();
      }
      ctx.fillStyle = th.overlay; ctx.fillRect(0, 0, W, H);   // time-of-day tint
      if (!stateRef.current.lightsOn) {
        ctx.fillStyle = 'rgba(6,10,24,0.55)'; ctx.fillRect(0, 0, W, H);   // lights off → dim
        // warm desk-lamp pools at the desks that are working
        for (const id of Object.keys(META)) {
          const d = (ds || []).find(x => x.id === id);
          if (d?.status === 'working') {
            const desk = META[id].desk; const g = ctx.createRadialGradient(desk.x, desk.y, 6, desk.x, desk.y, 70);
            g.addColorStop(0, 'rgba(255,196,110,0.32)'); g.addColorStop(1, 'rgba(255,196,110,0)');
            ctx.fillStyle = g; ctx.fillRect(desk.x - 70, desk.y - 40, 140, 130);
          }
        }
      }

      // progress rail
      px(ctx, 0, H - 4, W, 4, '#c2ccdb');
      const done = c?.status === 'done', failed = c?.status === 'failed';
      px(ctx, 0, H - 4, W * (c?.progress || 0) / 100, 4, failed ? '#ef4444' : done ? '#22c55e' : '#6366f1');

      // ── move DOM labels + bubbles to follow each agent (crisp, no React re-render) ──
      for (const id of ORDER) {
        const a = agents.current[id]; const el = labelRefs.current[id];
        // Project world → screen through the same camera the canvas used.
        const sx = W / 2 + (a.x - cam.current.x) * cam.current.s;
        const sy = H / 2 + (a.y + 14 - cam.current.y) * cam.current.s;
        if (el) { el.style.left = `${(sx / W) * 100}%`; el.style.top = `${(sy / H) * 100}%`;
          const vis = sx > -40 && sx < W + 40 && sy > -40 && sy < H + 40;
          el.style.display = vis ? '' : 'none';
          const b = el.querySelector('[data-bubble]') as HTMLElement; if (b) { b.textContent = a.bubble || ''; b.style.opacity = a.bubble ? '1' : '0'; } }
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => { stopped = true; cancelAnimationFrame(raf); };
  }, []);

  const running = cur?.status === 'running';
  const byId: Record<string, Desk> = {}; (desks || []).forEach(d => (byId[d.id] = d));

  // Finance billing pill — total per-provider spend + the preflight path decision, read from the
  // snapshot feed (works for both live and durable-reconstructed rooms; $ is parsed from the detail).
  const finFeed = (cur?.feed || []).filter((f: any) => f?.persona === 'finance');
  const spend = finFeed.filter((f: any) => f?.event === 'bill').reduce((s: number, f: any) => {
    const m = /\$([0-9.]+)/.exec(f?.detail || ''); return s + (m ? parseFloat(m[1]) : 0);
  }, 0);
  // REAL recipe from the job label ("<recipe> · <file>", set by the backend) — NOT a guess. The old
  // code hardcoded 'seedance' for any finance detail that didn't literally contain "avatar-lipsync",
  // so UGC/B-Roll/avatar jobs were mislabeled "path: seedance". Fall back to the heuristic only if
  // there is no label.
  const finDecision = finFeed.find((f: any) => f?.event === 'done')?.detail || '';
  const finPath = ((cur?.label || '').split(' · ')[0].trim())
    || (/avatar-lipsync/i.test(finDecision) ? 'avatar-lipsync' : (finDecision ? 'seedance' : ''));

  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-[#e9edf4]">
      <div className="absolute top-2 right-2 z-30 flex items-center gap-2">
        {(spend > 0 || finPath) && (
          <button onClick={() => onSelectPersona?.('finance')} title="See Finance — preflight decision + per-provider billing"
            className="text-[11px] font-medium px-2 py-1 rounded-full bg-amber-500/90 text-white cursor-pointer hover:ring-2 hover:ring-amber-300/60 transition">
            💰 ${spend.toFixed(2)}{finPath ? ` · path: ${finPath}` : ''}
          </button>
        )}
        <span className="text-[11px] px-2 py-1 rounded-full bg-slate-900/60 text-white capitalize">{phase}</span>
        <button onClick={() => setLightsOn(v => !v)} title={lightsOn ? 'Turn lights off' : 'Turn lights on'}
          className={`text-sm w-7 h-7 rounded-full flex items-center justify-center ${lightsOn ? 'bg-amber-300 text-amber-900' : 'bg-slate-700 text-slate-300'}`}>
          {lightsOn ? '💡' : '🌙'}
        </button>
        <div className={`text-[11px] font-medium flex items-center gap-1.5 px-2 py-1 rounded-full ${running ? 'bg-emerald-500/90 text-white' : 'bg-slate-600/80 text-white'}`}>
          <span className={`w-2 h-2 rounded-full ${running ? 'bg-white animate-pulse' : 'bg-slate-300'}`} />
          {running ? `working · ${cur?.progress ?? 0}%` : 'team idle — hanging out'}
        </div>
      </div>
      <div className="relative w-full" style={{ aspectRatio: `${W}/${H}` }}>
        <canvas ref={canvasRef} width={W} height={H} className="absolute inset-0 w-full h-full" style={{ imageRendering: 'pixelated' }} />
        {/* one label per agent — positioned by the loop to follow them */}
        {ORDER.map(id => {
          const d = byId[id]; const role = (d?.role || id).replace(/\s*\(.*\)/, '');
          return (
            <div key={id} ref={el => { labelRefs.current[id] = el; }}
              className="absolute z-20 -translate-x-1/2 flex flex-col items-center" style={{ left: '50%', top: '50%' }}>
              <div data-bubble className="mb-0.5 text-[13px] leading-none transition-opacity pointer-events-none" style={{ opacity: 0 }} />
              <button onClick={() => onSelectPersona?.(id)}
                title={d?.authority?.used === false
                  ? `Advisory only — this lane discards ${role}'s output${d.authority.note ? `: ${d.authority.note}` : ''}`
                  : "See this role's work log"}
                className={`px-1 py-0.5 rounded text-[8.5px] font-semibold shadow-sm border whitespace-nowrap cursor-pointer hover:ring-2 hover:ring-primary/50 transition
                ${d?.authority?.used === false ? 'bg-white/60 text-slate-400 border-slate-200 line-through decoration-slate-300'
                  : d?.status === 'working' ? 'bg-white text-emerald-700 border-emerald-300' : 'bg-white/75 text-slate-600 border-slate-200'}`}>
                {React.createElement(personaIcon(d?.role || role), { className: 'inline w-2.5 h-2.5 mr-0.5 align-[-1px]' })}{role}
              </button>
              {d?.authority?.used === false && (
                <span className="mt-0.5 px-1 rounded text-[7px] font-bold bg-slate-500/80 text-white whitespace-nowrap">ADVISORY</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PixelOffice;
